/* eslint-disable react/display-name */
import React, { memo, forwardRef, useContext, useState } from "react"
import PropTypes from "prop-types"
import { get } from "lodash"
import { v4 as uuid } from "uuid"
import { Icon } from "@4cplatform/elements/Atoms"
import { P, SmallText } from "@4cplatform/elements/Typography"
import { useTranslations } from "@4cplatform/elements/Translations"

// Helpers
import { breakpoints, colours, formatLabelForTestID } from "@4cplatform/elements/Helpers"

// Components
import { Modal } from "@4cplatform/elements/Molecules"
import { QuotationSummaryContext } from "../JourneyPage/pages/PMI/Quote/QuotationSummary/context/quotationSummary.context"
import QuoteProviderLogo from "./quotationSummaryTable.providerLogo"
import {
  TableHeaderColumnsWrapper,
  TableColumn,
  TableHeaderRows,
  TableRow,
  TableCell,
  QuoteReference,
  QuoteProviderWrapper,
  IndicatorWrapper,
  QuoteProviderInfo,
  IconWrapper
} from "./quotationSummaryTable.styles"
import { getUnderwritingHelperText } from "../JourneyPage/pages/PMI/Quote/QuoteComparison/quoteComparison.helpers"
import { getInfoMessage } from "../JourneyPage/pages/PMI/Quote/QuotationSummary/quotationSummary.helpers"

const QuotationSummaryTableHeader = forwardRef(({ isHeaderFixed }, ref) => {
  const { quotes, selectedQuote, setProviderInfoModal } = useContext(QuotationSummaryContext)
  const t = useTranslations()
  const [axaOneMonthFreePromoModalOpen, setAxaOneMonthFreePromoModalOpen] = useState(false)
  const width = document.body.clientWidth >= breakpoints.medium ? "800px" : "100%"

  const handleAxaOneMonthFreePromoOpen = () => setAxaOneMonthFreePromoModalOpen(true)
  const handleAxaOneMonthFreePromoClose = () => setAxaOneMonthFreePromoModalOpen(false)

  return (
    <TableHeaderColumnsWrapper ref={ref} isHeaderFixed={isHeaderFixed}>
      {quotes.map(quote => {
        const providerLogoSrc = get(quote, "provider.logo_file_path", "")
        const providerLogoAltTxt = get(quote, "provider.name", "")
        const isQuoteSelected = selectedQuote?.id === quote.id
        const isVitality = get(quote, "provider.provider_key", false) === "VITALITYHEALTH"
        const isSwitchSave = get(quote, "quote_type", null) === "switch_save"
        const isMoriPlus = get(quote, "flags.VITALITY_MORI_PLUS", false)
        const isBupaHealthLifestyle = get(quote, "flags.BUPA_HEALTH_LIFESTYLE_DISCOUNT", false)
        const isAxaOneMonthFreePromotion = get(quote, "flags.AXA_ONE_MONTH_FREE_PROMO", false)

        const rowProps = {
          bgColour: isQuoteSelected ? "green" : "veryFaintGrey",
          noBorder: isQuoteSelected
        }

        const cellProps = {
          colour: isQuoteSelected ? "white" : null
        }

        return (
          <TableColumn key={uuid()} data-testid="quotation_summary_table_quote_header">
            <TableHeaderRows>
              <TableRow {...rowProps}>
                <TableCell {...cellProps}>
                  {isSwitchSave && (
                    <div
                      className="tag"
                      style={{
                        position: "absolute",
                        right: "5px",
                        top: "5px",
                        background: "#197DA4",
                        padding: "5px",
                        "border-radius": "5px",
                        color: "#fff",
                        "font-size": "10px"
                      }}
                    >
                      Switch & save up to 10%
                    </div>
                  )}
                  {isMoriPlus && (
                    <div
                      className="tag"
                      style={{
                        position: "absolute",
                        right: "5px",
                        top: "5px",
                        background: "#197DA4",
                        padding: "5px",
                        "border-radius": "5px",
                        color: "#fff",
                        "font-size": "10px"
                      }}
                    >
                      Mori+ Discount Applied
                    </div>
                  )}
                  {isBupaHealthLifestyle && (
                    <div
                      className="tag"
                      style={{
                        position: "absolute",
                        right: "5px",
                        top: "5px",
                        background: "#197DA4",
                        padding: "5px",
                        "border-radius": "5px",
                        color: "#fff",
                        "font-size": "10px"
                      }}
                    >
                      Health and Lifestyle Discount Applied
                    </div>
                  )}
                  {isAxaOneMonthFreePromotion && (
                    <>
                      <button
                        type="button"
                        className="tag"
                        style={{
                          position: "absolute",
                          right: "5px",
                          top: "5px",
                          background: "#197DA4",
                          padding: "5px",
                          "border-radius": "5px",
                          color: "#fff",
                          cursor: "pointer",
                          border: 0,
                          fontSize: "10px"
                        }}
                        onClick={handleAxaOneMonthFreePromoOpen}
                      >
                        1 Month Free Promotion
                      </button>
                      {axaOneMonthFreePromoModalOpen && (
                        <Modal
                          width={width}
                          title="One Month Free Cover Offer (Sept-Nov 2026)"
                          onClose={handleAxaOneMonthFreePromoClose}
                        >
                          <P>
                            <strong>The Offer</strong>
                          </P>
                          <P>
                            AXA Health is giving new individual customers{" "}
                            <strong>one-month free insurance cover</strong> on eligible private
                            medical insurance plans with a start date between
                            <strong> 14 September and 13 November 2026</strong>.
                          </P>
                          <P>
                            If paying annually: You pay for 11 months (still get the 5% annual
                            discount)
                          </P>
                          <P>If paying monthly: Month 4 is free</P>
                          <br />
                          <P>
                            <strong>Who Can Claim</strong>
                          </P>
                          <P>You must:</P>
                          <ul>
                            <li>Be over 18</li>
                            <li>
                              Be a completely new customer (haven't had any AXA PPP or AXA Health
                              plan in the last 3 months)
                            </li>
                            <li>
                              Buy an eligible policy from an authorised Broker during the offer
                              period
                            </li>
                            <li>Start your plan during the offer period</li>
                          </ul>
                          <br />
                          <P>
                            <strong>Eligible Plans - (including child-only policies)</strong>
                          </P>
                          <ul>
                            <li>Personal Health</li>
                            <li>Health for You</li>
                            <li>inSpire Health Plan</li>
                          </ul>
                          <br />
                          <P>
                            <strong>Important Restrictions</strong>
                          </P>
                          <ul>
                            <li>One free month per policy only</li>
                            <li>Cannot be combined with other offers</li>
                            <li>Non-transferable, no cash alternative</li>
                            <li>Only available through authorised Brokers</li>
                            <li>AXA can withdraw or amend the offer anytime</li>
                          </ul>
                          <br />
                          <P>
                            <strong>Key Disclaimers</strong>
                          </P>
                          <ul>
                            <li>
                              Your data will be used to administer the offer (complies with Data
                              Protection Act 2018)
                            </li>
                            <li>
                              AXA isn't liable for losses related to the offer, except where legally
                              required
                            </li>
                            <li>English law applies</li>
                          </ul>
                          <br />
                          <P>
                            <strong>Offer Period</strong>
                          </P>
                          <P>Opening: 00:00 BST on 14 September 2026</P>
                          <P>Closing: 23:59 GMT on 13 November 2026</P>
                        </Modal>
                      )}
                    </>
                  )}
                  <QuoteProviderWrapper>
                    <IconWrapper>
                      <Icon
                        icon="help-circle-outline"
                        colour={colours.blue}
                        onClick={() =>
                          setProviderInfoModal({
                            open: true,
                            data: quote
                          })
                        }
                        role="button"
                        transition="none"
                        margin="0"
                        data-testid={`${formatLabelForTestID(providerLogoAltTxt)}-helper_text-icon`}
                      />
                    </IconWrapper>
                    <QuoteProviderLogo src={providerLogoSrc} alt={providerLogoAltTxt} />
                    <QuoteProviderInfo>
                      <P margin="0" colour={isQuoteSelected ? "white" : ""}>
                        <strong> {t(get(quote, "provider.name", "-"))}</strong>
                      </P>
                      <SmallText margin="0" colour={isQuoteSelected ? "white" : ""}>
                        {t(get(quote, "product_name", "-"))}
                      </SmallText>
                    </QuoteProviderInfo>
                  </QuoteProviderWrapper>
                </TableCell>
              </TableRow>
              <TableRow {...rowProps}>
                <TableCell {...cellProps}>
                  <QuoteReference>{get(quote, "reference", "-")}</QuoteReference>
                </TableCell>
              </TableRow>
              <TableRow {...rowProps}>
                <TableCell {...cellProps} scrollableCol>
                  <IndicatorWrapper>
                    {isVitality && isSwitchSave && (
                      <P
                        className="quote-data"
                        helperPosition="right"
                        helperText={getInfoMessage("vitality_switch_save")?.content || ""}
                        helperTitle={getInfoMessage("vitality_switch_save")?.title || ""}
                      >
                        {t(get(quote, "underwriting_style"))} {t(get(quote, "underwriting_type"))}
                      </P>
                    )}
                    {isVitality && isMoriPlus && (
                      <P
                        className="quote-data"
                        helperPosition="right"
                        helperText={getInfoMessage("vitality_mori_plus")?.content || ""}
                        helperTitle={getInfoMessage("vitality_mori_plus")?.title || ""}
                      >
                        {t(get(quote, "underwriting_style"))} Moratorium+
                      </P>
                    )}
                    {!isSwitchSave && !isMoriPlus && (
                      <>
                        {getUnderwritingHelperText(get(quote, "flags", {})).map(helper => (
                          <P helperPosition="left" margin="0" {...helper} />
                        ))}
                        <P className="quote-data">
                          {t(get(quote, "underwriting_style"))} {t(get(quote, "underwriting_type"))}
                        </P>
                      </>
                    )}
                  </IndicatorWrapper>
                </TableCell>
              </TableRow>
            </TableHeaderRows>
          </TableColumn>
        )
      })}
    </TableHeaderColumnsWrapper>
  )
})

QuotationSummaryTableHeader.defaultProps = {
  isHeaderFixed: false
}

QuotationSummaryTableHeader.propTypes = {
  isHeaderFixed: PropTypes.bool
}

export default memo(QuotationSummaryTableHeader)
